import { createSystem, defaultConfig, defineConfig, defineSlotRecipe, Token, Tokens } from '@chakra-ui/react';
import { drawerAnatomy } from '@chakra-ui/react/anatomy';

// Special Chakrea css class, see description below.
export const chakraResetClassPrefix = 'chakra-reset';

// Our css prop requires our theme, but chakra expects theirs.
// As an easy solution, lets not allow the css prop in Chakra components.
export type BannedChakraProps = 'css';

const drawerRecipe = defineSlotRecipe({
  slots: drawerAnatomy.keys(),
  variants: {
    size: {
      full: {
        content: {
          maxW: 'calc(100vw - 128px)',
        },
      },
    },
  },
});

const baseFontSize = 13;
const px2rem = (px: number): string => `${Number(px / baseFontSize).toFixed(3)}rem`;

const green = {
  '50': { value: '#e6fff5' },
  '100': { value: '#c9f7e6' },
  '200': { value: '#abefd8' },
  '300': { value: '#8ae7cb' },
  '400': { value: '#62dfbe' },
  '500': { value: '#22d6b2' },
  '600': { value: '#21a788' },
  '700': { value: '#1b7b62' },
  '800': { value: '#12513e' },
  '900': { value: '#072b1f' },
} as const;

const config = defineConfig({
  preflight: {
    // This class gets added to all selectors in the Chakra reset layer.
    // Adding this prevents Chakra's reset styles from leaking into txture.
    // For Chakra components to work, all root Chakra ui elements need this class.
    // FIXME: Come up with a better solution to blend these two theming methods.
    scope: `.${chakraResetClassPrefix}`,
    level: 'parent',
  },
  theme: {
    tokens: {
      spacing: {
        '0': { value: '0' },
        '1': { value: '4px' },
        '2': { value: '8px' },
        '3': { value: '12px' },
        '4': { value: '16px' },
        '5': { value: '20px' },
        '6': { value: '24px' },
        '8': { value: '32px' },
        '12': { value: '48px' },
        '14': { value: '56px' },
        '16': { value: '64px' },
        '18': { value: '72px' },
        '24': { value: '96px' },
        '32': { value: '128px' },
      },
      colors: {
        white: { value: '#ffffff' },
        black: { value: '#000000' },
        green,
        primary: green,
        grey: {
          '50': { value: '#f3f3f4' },
          '100': { value: '#d7d7d9' },
          '200': { value: '#bdbdbf' },
          '300': { value: '#a2a2a5' },
          '400': { value: '#89898c' },
          '500': { value: '#707074' },
          '600': { value: '#59595c' },
          '700': { value: '#424246' },
          '800': { value: '#2d2d31' },
          '900': { value: '#1a1a1d' },
        },
        blue: {
          '50': { value: '#e8f3f9' },
          '100': { value: '#c1ddef' },
          '500': { value: '#0084ce' },
          '600': { value: '#086ba7' },
          '800': { value: '#0e3d5d' },
        },
        red: {
          '50': { value: '#fef2f2' },
          '100': { value: '#ffd3cf' },
          '200': { value: '#ffb4a7' },
          '300': { value: '#ff977b' },
          '400': { value: '#ff7b4a' },
          '500': { value: '#f56200' },
          '600': { value: '#cb4612' },
          '700': { value: '#9f2e15' },
          '800': { value: '#741912' },
          '900': { value: '#4a0807' },
        },
        orange: {
          '50': { value: '#fff3e0' },
          '100': { value: '#ffe2b6' },
          '500': { value: '#ff9102' },
          '600': { value: '#d06e10' },
          '800': { value: '#72330e' },
        },
        yellow: {
          '50': { value: '#fefce8' },
          '100': { value: '#fffbc7' },
          '400': { value: '#fff659' },
          '500': { value: '#e1c343' },
          '700': { value: '#976924' },
          '800': { value: '#6d4217' },
        },
      },
      lineHeights: {
        none: { value: 1 },
        dense: { value: 1.125 },
        tight: { value: 1.25 },
        snug: { value: 1.375 },
        normal: { value: 1.5 },
        relaxed: { value: 1.625 },
        loose: { value: 2 },
      },
      fontSizes: {
        xl9: { value: px2rem(54) },
        xl8: { value: px2rem(40) },
        xl7: { value: px2rem(30) },
        xl6: { value: px2rem(24) },
        xl5: { value: px2rem(20) },
        xl4: { value: px2rem(18) },
        xl3: { value: px2rem(16) },
        xl2: { value: px2rem(15) },
        xl: { value: px2rem(14.5) },
        lg: { value: px2rem(14) },
        md: { value: px2rem(baseFontSize) },
        sm: { value: px2rem(12) },
        xs: { value: px2rem(11) },
        xs2: { value: px2rem(10) },
      },
      fontWeights: {
        normal: { value: 400 },
        semiBold: { value: 600 },
      },
      letterSpacings: {
        tighter: { value: '-0.05em' },
        tight: { value: '-0.025em' },
        normal: { value: '0em' },
        wide: { value: '0.025em' },
        wider: { value: '0.05em' },
        widest: { value: '0.1em' },
      },
      shadows: {
        '1': { value: '0 2px 2px 0 rgba(0, 0, 0, 0.16), 0 0 2px 0 rgba(0, 0, 0, 0.12)' },
        floating: { value: 'rgb(9 30 66 / 25%) 0px 4px 8px -2px, rgb(9 30 66 / 31%) 0px 0px 1px' },
      },
      zIndex: {
        portal: { value: 1001 },
        dropdown: { value: 1001 },
        sticky: { value: 1001 },
        banner: { value: 1001 },
        overlay: { value: 1001 },
        modal: { value: 1001 },
      },
    },
    semanticTokens: {
      colors: {
        coloredBg: { value: 'linear-gradient(17.27deg, #fbf7e4 0%, #1dbe9f 58.55%, #00a186 100%)' },
        text: { value: '{colors.grey.700}' },
        eol: {
          outdated: {
            background: { value: '{colors.red[50]}' },
            border: { value: '{colors.red[500]}' },
          },
          soonOutdated: {
            background: { value: '{colors.orange[50]}' },
            border: { value: '{colors.orange[500]}' },
          },
        },
        status: {
          info: {
            main: { value: '{colors.blue.500}' },
            background: { value: '{colors.blue.50}' },
            hd: { value: '{colors.blue.600}' },
            text: { value: '{colors.blue.800}' },
          },
          success: {
            main: { value: '{colors.green.500}' },
            background: { value: '{colors.green.50}' },
            hd: { value: '{colors.green.600}' },
            text: { value: '{colors.green.800}' },
          },
          warning: {
            main: { value: '{colors.yellow.500}' },
            background: { value: '{colors.yellow.50}' },
            hd: { value: '{colors.yellow.700}' },
            text: { value: '{colors.yellow.800}' },
          },
          error: {
            main: { value: '{colors.red.500}' },
            background: { value: '{colors.red.50}' },
            hd: { value: '{colors.red.600}' },
            text: { value: '{colors.red.800}' },
          },
        },
        card: {
          background: { value: '{colors.white}' },
        },
      },
      lineHeights: {
        body: { value: '{lineHeights.normal}' },
        heading: { value: '{lineHeights.dense}' },
        longText: { value: '{lineHeights.snug}' },
      },
      sizes: {
        headerHeight: { value: '50px' },
        maxSmallContainerWidth: { value: '1536px' },
      },
    },
    breakpoints: {
      widthSmall: '1519px',
      widthMobile: '1349px',
    },
    slotRecipes: {
      drawer: drawerRecipe,
    },
  },
});

export const theme = createSystem(defaultConfig, config);
// default export is needed for chakra typegen
export default theme;

export type ThemeColors = Tokens['colors'];

export type Theme = {
  token: (path: Token, fallback?: string) => string;
};

/**
 * Special theme for our ACN MyNav instances.
 * The theme is activated via a specific txture.properties setting.
 */

const myNavBlue = {
  '50': { value: '#e6edfd' },
  '100': { value: '#d3deff' },
  '200': { value: '#aec2ff' },
  '300': { value: '#819fff' },
  '400': { value: '#4b7aff' },
  '500': { value: '#004dff' },
  '600': { value: '#0a3bab' },
  '700': { value: '#0b257b' },
  '800': { value: '#05164a' },
  '900': { value: '#06102f' },
} as const;

const myNavTheme = createSystem(defaultConfig, config, {
  theme: {
    tokens: {
      colors: {
        'my-nav-blue': myNavBlue,
        primary: myNavBlue,
      },
    },
    semanticTokens: {
      colors: {
        coloredBg: { value: 'linear-gradient(17.27deg, rgb(140, 175, 255) 0%, rgb(0, 77, 255) 75%)' },
      },
    },
    breakpoints: {
      widthSmall: '1819px',
      widthMobile: '1649px',
    },
  },
});

export const getTheme = (useMyNavStyling: boolean) => (useMyNavStyling ? myNavTheme : theme);
